import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import styled from "@emotion/styled";
import {
  getPanelElement,
  getPanelGroupElement,
  ImperativePanelHandle,
  PanelGroup,
  PanelGroupProps,
  PanelResizeHandle,
} from "react-resizable-panels";
import useMeasure from "react-use/lib/useMeasure";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import useThrottledCallback from "@/domains/react/useThrottledCallback";
import { MdsPanel } from "@/design-system/components/panel/MdsPanel";
import { mdsPanelWidths } from "@/design-system/foundations";
import { ActiveSessions } from "@/components/active-session";

interface SplitViewProps extends PropsWithChildren {
  className?: string;
  style?: React.CSSProperties;
  hasBorder?: boolean;
  animate?: boolean;
  transition?: boolean;
  dataTestId?: string;
  sidePanel?: ReactNode;
}

const SPLIT_VIEW_PANEL_GROUP_ID = "split-view-panel-group";
const PANEL_COUNT = 2;

export const SplitView: FC<SplitViewProps> = observer(
  ({ children, hasBorder = false, dataTestId = "", sidePanel }) => {
    const { store } = useAppStore();
    const { isSidePanelOpen, isMainPanelOpen, isSplitView, canRenderSplitView } = store.sidePanel;
    const showResizeHandle = isSplitView && canRenderSplitView;

    const [setContainerRef, { width }] = useMeasure<HTMLElement>();
    useLayoutEffect(() => {
      const el = getPanelGroupElement(SPLIT_VIEW_PANEL_GROUP_ID);
      if (el) setContainerRef(el);
    });

    const minPanelWidth = useMemo(
      () => (mdsPanelWidths().min / (width - mdsPanelWidths().gutter * PANEL_COUNT)) * 100,
      [width]
    );

    const focusMainPanel = useThrottledCallback(() => {
      store.sidePanel.setSidePanelFocused(false);
    }, 500);

    const focusSidePanel = useThrottledCallback(() => {
      store.sidePanel.setSidePanelFocused(true);
    }, 500);

    const [setSidePanelRef, { width: sidePanelWidth }] = useMeasure<HTMLElement>();
    useEffect(() => {
      store.sidePanel.setSidePanelWidth(Math.ceil(sidePanelWidth));
    }, [sidePanelWidth, store.sidePanel]);

    const SidePanelId = "right";
    const setSidePanelHandleRef = useCallback(
      (_: ImperativePanelHandle) => {
        const el = getPanelElement(SidePanelId);
        if (el) setSidePanelRef(el);
      },
      [setSidePanelRef]
    );

    // Side panel default width: no wider than the average width of center panes after opening.
    const paneCount = store.navigation.tabs.panes.length || 1;
    const maxSidePercent = 100 / (paneCount + 1);
    const sidePanelDefaultSize = Math.min(40, maxSidePercent);
    const mainPanelDefaultSize = 100 - sidePanelDefaultSize;

    return (
      <Container
        id={SPLIT_VIEW_PANEL_GROUP_ID}
        direction="horizontal"
        hasSidePanel={isSplitView}
        data-component="SplitView"
      >
        {isMainPanelOpen && (
          <MainPanel
            id="main"
            order={1}
            data-test-id={dataTestId}
            defaultSize={mainPanelDefaultSize}
            hasBorder={hasBorder}
            onFocus={focusMainPanel}
            onClick={focusMainPanel}
            minSize={minPanelWidth}
            data-component="MainPanel"
          >
            <CenterPanel data-component="CenterPanel">
              {children}
              <ActiveSessions />
            </CenterPanel>
          </MainPanel>
        )}
        {showResizeHandle && (
          <StyledPanelResizeHandle data-component="StyledPanelResizeHandle">
            <ResizeHandle data-component="ResizeHandle" />
          </StyledPanelResizeHandle>
        )}
        {isSidePanelOpen && (
          <SidePanelContainer
            ref={setSidePanelHandleRef}
            id={SidePanelId}
            order={2}
            defaultSize={sidePanelDefaultSize}
            onFocus={focusSidePanel}
            onClick={focusSidePanel}
            minSize={minPanelWidth}
            data-component="SidePanelContainer"
          >
            {sidePanel}
          </SidePanelContainer>
        )}
      </Container>
    );
  }
);

interface ContainerProps extends PanelGroupProps {
  hasSidePanel: boolean;
}

const Container = styled(PanelGroup, {
  shouldForwardProp: prop => prop !== "hasSidePanel",
})<ContainerProps>(({ hasSidePanel }) =>
  hasSidePanel
    ? {
        display: "flex",
        flexDirection: "row",
        height: "100%",
        width: "100%",
        gap: "2px",
      }
    : {
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
        justifyContent: "center",
        margin: "0 auto",
        maxWidth: "100%",
        width: "100%",
      }
);

interface MainPanelProps {
  hasBorder: boolean;
}

const MainPanel = styled(MdsPanel, {
  shouldForwardProp: prop => prop !== "hasBorder",
})<MainPanelProps>(({ hasBorder, theme }) => ({
  alignSelf: "center",
  boxSizing: "border-box",
  display: "flex",
  height: "100%",
  overflow: "hidden",
  border: "none",

  ...(hasBorder
    ? {
        "@media (max-width: 899px)": {
          width: "100%",
        },

        "@media (min-width: 900px)": {
          border: `1px solid ${theme.colors.border.default}`,
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          boxShadow: "0px 2px 4px rgba(69, 79, 104, 0.1)",
        },
      }
    : {
        "@media (max-width: 900px)": {
          width: "calc(100% - 16px)",
        },
      }),
}));

const CenterPanel = styled.div({
  boxSizing: "border-box",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
});

const StyledPanelResizeHandle = styled(PanelResizeHandle)`
  border-radius: 100px;
  cursor: ew-resize;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;

  &:hover, &[data-resize-handle-state="hover"], &[data-resize-handle-state="drag"] {
    background: ${({ theme }) => theme.colors.surface.pane};
    transition: 0.2s ease-in;
    border-color: ${({ theme }) => theme.colors.border.default};

    "> div": {
      border-color: ${({ theme }) => theme.colors.border.default};
    },
  },
`;

const ResizeHandle = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  overflow: hidden;
  width: 2px;
`;

const SidePanelContainer = styled(MdsPanel)`
  position: relative;
`;

// Removed the pulsing overlay used for Heads Up generation in favor of an inline loader bar
